import statePages from "config/state-pages.json";
import partnerPages from "config/partner-pages.json";
import states from "public/states.json";
import { Services } from "utils/account/services";

// Re-export other type files
export * from "./bill-reduction";
export * from "./mortgage-rates";
export * from "./property";
export * from "./property-equity";
export * from "./client";
export * from "./cms";

export type StateCode = keyof typeof states;
export type StatesWithStatePage = keyof typeof statePages;
export type StatesWithStateSpecificPage = "GA" | "NY";
export type PartnersWithPartnerPage = keyof typeof partnerPages;

export interface ICategory {
	id: string;
	title: string;
	body: string;
}

export interface CategoryByIdRes {
	category: ICategory;
	sections: SectionsInCategory[];
}

export interface ISection {
	id: string;
	categoryId: string;
	name: string;
}

export interface IArticle {
	body: string;
	id: string;
	label_names: string[];
	name: string;
	promoted: boolean;
	section_id: string;
	title: string;
	// url: string;
	// html_url: string;
	// author_id: number;
	// comments_disabled: boolean;
	// draft: boolean;
	// position: number;
	// vote_sum: number;
	// vote_count: number;
	// created_at: string;
	// updated_at: string;
	// source_locale: string;
	// locale: string;
	// outdated: boolean;
	// outdated_locales: string[];
	// edited_at: string;
	// user_segment_id: number;
	// permission_group_id: number;
}

export interface FoundArticles {
	results: FoundArticleResults[];
}

export interface FoundArticleResults {
	id: string;
	title: string;
	body: string;
}

export interface HelpCenterHome {
	promoted: FoundArticleResults[];
	categories: ICategory[];
}

export interface SectionsInCategory {
	id: string;
	title: string;
	articles: ArticlesInSections[];
}

export interface ArticlesInSections {
	id: string;
	name: string;
	promoted: string;
	label_names: string[];
}

export interface HelpCenterBreadcrumbs {
	name: string;
	link: string;
}

export interface GeocodeData {
	address: string;
	line2: string;
	city: string;
	county: string;
	state: string;
	zipcode: string;
	fips?: string;
	apn?: string;
}

export interface LegalData {
	signatureData: string;
	agreementUrl: string;
}

export type ContingencyConfig = {
	appeal_contingency: number;
	appeal_flat: number;
	exemption_primary_contingency: number;
	exemption_primary_flat: number;
};

export interface GeoLevelSavingsData {
	county: string;
	amt: number;
	inService: boolean;
	pastDeadline?: boolean;
	softDeadline?: boolean;
	out_of_season?: boolean;
	savingsType: "county" | "state";
	tax_year: string | null;
	deadline?: string;
	exemptionsInService: boolean;
	contingency: ContingencyConfig;
	exemptionSavings?: ExemptionsSavingsResponse | undefined;
}

export interface ExactSavingsData {
	address: string;
	apn: string;
	county: string;
	amt: number;
	saledate?: string;
	fips: string;
	income_producing?: boolean;
	score?: number;
	discount?: number;
	inService?: boolean;
	pastDeadline?: boolean;
	softDeadline?: boolean;
	out_of_season?: boolean;
	owner_name?: string;
	class_code_in_service?: boolean;
	exemption_savings_type: "county" | "state";
	exemption_capped?: 1 | 0;
	exemption_capped_no_chance_of_savings?: 1 | 0;
	deadline?: string;
	assessment?: number;
	recommended?: number;
	taxrate?: number;
	savings_achieved: number | null;
	tax_year: string | null;
	ownerEmail: string | null;
	exemptionSavings: ExemptionsSavingsResponse | undefined;
	exemptionsInService: boolean;
	exemptions_retro_target: boolean;
	contingency: ContingencyConfig;
	show_savings_on_website: boolean;
	mailer_has_savings: boolean;
	video_url: string | null;
	video_name: string | null;
	show_video: boolean | null;
	use_group: string | null;
	is_potential_triple_net: boolean | null;
}

export type SavingsData = GeoLevelSavingsData | ExactSavingsData;

export interface MultiAddress {
	address: string;
	city: string;
	state: string;
	zip: string;
	county: string;
	apn: string;
	company?: string;
	company_title?: string;
	savings_estimate: number | null;
}

export interface MultiRes {
	email: string;
	fname: string;
	lname: string;
	phone: string;
	company: string;
	signature: any | null;
	contingency: string | null;
	custom_service_agreement: number;
	addresses: MultiAddress[];
	is_leftovers: 1 | 0;
	customer?: {
		id: number;
		referral_code: string | undefined;
		property_count: number;
	};
	created_new_customer: boolean;
	confirm_otc: boolean;
	savings_estimate: number;
	partner_ref: string | null;
	creation_source: string | null;
}

export interface AppealStatusData {
	address: string;
	address2: string | undefined;
	city: string;
	county: string;
	use_group: string | null;
	company: string;
	appeal_id: number;
	use_info: string | null;
	first_name: string;
	last_name: string;
	auth_url: string;
	start_date: string;
	end_date: string;
	last_signed: string;
	stage: string;
	state: string;
	tax_year: number;
	zip_code: string;
	hearing_status: string;
	market_value_to_beat: number;
	original_market_value: number;
	final_market_value: number;
	final_tax_savings: string | null;
	tax_rate: number;
	authorization_id: number;
}

export interface UserData {
	id: number;
	email: string;
	phone: string;
	first_name: string;
	last_name: string;
	preferences_status_update_freq: number;
	insurance_waitlist: number;
	has_outstanding_invoice: number;
	referral_code: string;
	referral_count: number;
	account_credit: number;
	num_properties: number;
	created_at: string;
	customer_mailing_address: string;
	customer_mailing_address_2: string;
	customer_mailing_city: string;
	customer_mailing_state: string;
	customer_mailing_zip: string;
	sms_consent: 1 | 0;
	care_takeover_token: string | null;
	account_tier: string | null;
	interested_tier: string | null;
	pricing_modal_seen: 1 | 0;
	multi_dashboard_enabled: 1 | 0;
	year_over_year_customer: 1 | 0;
	num_active_properties: number | null;
	customer_accepted_reduced_contingency: 1 | 0;
	referral_program: string;
	account_rep: number | null;
	tax_bill_payments_enabled: 1 | 0;
	has_answered_heardabout: 1 | 0;
	can_upgrade_to_premium: 1 | 0;
	active_subscription: ActiveSubscription[];
	partner_ref: string | null;
	referral_programs_breakdown: ReferralProgramBreakdown[];
}

export interface ReferralProgramBreakdown {
	program: string;
	count: number;
}

export interface ActiveSubscription {
	product: string;
	subscriptionId: number;
}

export interface IAddress {
	address: string;
	line2: string;
	city: string;
	state: string;
	zipcode: string;
	county?: string;
	fips?: string;
	apn?: string;
}

export interface ContactInfo {
	firstName: string;
	lastName: string;
	phoneNum: string | null;
	email: string;
	company: string;
	title: string;
	companyType: string;
}

export interface INavLink {
	label: string;
	href: string;
	external?: boolean;
}

export interface AutocompleteItem {
	score: number;
	label: string;
	params: {
		fips: string;
		apn: string;
		taxaccountnumber: string;
		situsfullstreetaddress: string;
		situscity: string;
		situsstate: string;
		situszip5: string;
		county: string;
	};
	location?: {
		lat: number;
		lon: number;
	};
}

export interface ApnAutocompleteItem extends AutocompleteItem {
	labels: {
		apn: string;
		taxaccountnumber: string;
	};
}

export interface ApnAutocompleteRes {
	status: number;
	body: ApnAutocompleteItem[];
}

export interface AutocompleteRes {
	status: number;
	body: AutocompleteItem[];
}

export interface CaseStudyFetchResult {
	id: number;
	title: string;
	slug: string;
	description: string;
	hero_image: { url: string };
	body: string;
	tags: { id: number; list_item: string }[];
	published_at: string;
	prior_savings_value?: number;
	post_savings_value?: number;
	tax_savings_value?: number;
	assessment_reduction_value?: number;
	absoluteImgUrlFlag?: boolean;
}

export type AppealStatus =
	| "Monitoring"
	| "Processing"
	| "Verifying Ownership"
	| "Evaluating"
	| "Filing"
	| "Filed"
	| "Hearing Scheduled"
	| "Awaiting Results"
	| "Escalation Recommended"
	| "Litigation Recommended"
	| "Processing Escalation"
	| "Processing Litigation"
	| "Escalated"
	| "Evaluation Complete"
	| "Appeal Complete"
	| "Appeal Successful"
	| "Canceled";

export type ExemptionStatus =
	| "Already Has Homestead"
	| "Not Started"
	| "Started"
	| "Signed"
	| "On Hold"
	| "Verified"
	| "Ready to File"
	| "Filed"
	| "Active"
	| "Denied"
	| "Archive";

export type AppealTrackingStatus =
	| "Verified as Legal Owner"
	| "Assessment Details Received"
	| "Signed Up to Appeal"
	| "Appeal Created";

export type AppealHistoryStatus = AppealStatus | AppealTrackingStatus;

export type AppealHistoryItem =
	| {
			type: "status";
			status: AppealHistoryStatus;
			snapshot: Partial<
				Pick<
					PropertyDetailsAppeal,
					| "case_reason"
					| "reject_reason"
					| "final_market_value"
					| "market_value_to_beat"
					| "original_market_value"
				>
			>;
			created_at: string;
	  }
	| {
			type: "upload";
			question: string;
			files: Array<string>;
			created_at: string;
	  }
	| {
			type: "needs_attention_reason";
			reason: NeedsAttentionReasons;
			snapshot: Partial<
				Pick<PropertyDetailsAppeal, "status"> &
					Pick<
						PropertyDetails,
						| "verified_owner"
						| "legal_owner"
						| "auth_company"
						| "auth_company_type"
					>
			>;
			set?: boolean;
			remove?: boolean;
			created_at: string;
	  }
	| {
			type: "documents_reviewed";
			docs_failed_review_reason?: string;
			created_at: string;
	  }
	| {
			type: "agent_authorization_form_filed";
			created_at: string;
	  };

export type CountyInformationValueType =
	| { format: "string"; data: string }
	| { format: "link"; data: string }
	| { format: "phone"; data: string }
	| { format: "date"; data: string }
	| { format: "email"; data: string };

export type CountyInformationEntriesType = {
	[key: string]: CountyInformationValueType | CountyInformationEntriesType;
};

export type CountyDatesAndLinks = {
	county: string | null;
	portal_url: string | null;
	prop_search_url: string | null;
	assessor_title: string | null;
	tax_bill_issued_date: string | null;
	tax_bill_due_date_first: string | null;
	tax_bill_due_date_second: string | null;
	tax_bill_valuation_date: string | null;
	info_appeal_start_date: string | null;
	info_appeal_deadline_date: string | null;
	info_assessor_phone: string | null;
	info_assessor_email: string | null;
	info_clerk_url: string | null;
	info_clerk_title: string | null;
	info_clerk_phone: string | null;
	info_clerk_email: string | null;
};

export namespace PropertyTaxes {
	export type YearData = {
		property_id: number;
		tax_year: number;
		original_market_value: string | null;
		original_assessed_value: string | null;
		tax_bill_before_protest: string | null;
		ownwell_protested: 0 | 1 | null;
		ownwell_won: 0 | 1 | null;
		ownwell_approved: 0 | 1 | null;
		final_tax_savings: string | null;
		final_market_value: string | null;
		final_assessed_value: string | null;
		market_value_reduction: number | null;
		assessed_value_reduction: number | null;
		projected: 0 | 1;
	};

	export type CountyInfo = {
		taxacct: string | null;
	} & CountyDatesAndLinks;

	export type Response = {
		yearly_data: YearData[];
		county_info: CountyInfo | null;
	};
}

export namespace PropertyEquity {
	export type AmortizationEntry = {
		date: string;
		expected_remaining_principal: number;
		remaining_principal: number;
		interest_payment: number;
		principal_payment: number;
		extra_monthly_payment: number;
	};

	export type AmortizationYearlySummary = {
		year: number;
		principal: number;
		interest: number;
		balance: number;
	};

	export type Response = {
		mortgage_last_synced_at: string | null;
		mortgage_last_updated_at: string | null;
		last_sale_date_prop: string | null;
		last_sale_price_prop: string | null;
		principal_paid: number | null;
		principal_remaining: number | null;
		interest_paid: number | null;
		interest_remaining: number | null;
		mortgage_loan_type: string | null;
		mortgage_financing_type: string | null;
		mortgage_amount: number | null;
		mortgage_down_payment: number | null;
		mortgage_interest_rate: string | null;
		mortgage_term: number | null;
		mortgage_paid_off: OneZeroOrNull | true;
		mortgage_extra_monthly_payment: number | null;
		mortgage_refinance_date: string | null;
		next_payment: AmortizationEntry | null;
		debt: number | null;
		equity: number | null;
		amortization_schedule: AmortizationEntry[] | [];
	};
}
export namespace PropertyValue {
	export type MarketValuePrice = {
		date: string;
		timestamp: number;
		is_purchase_date: boolean;
		market_value: string | null;
		value_change: number | null;
	};

	export type Response = {
		value_change_since_purchase: number | null;
		market_value_history: MarketValuePrice[];
	};
}

export namespace Notifications {
	export type Notification<T = string> = {
		id: number;
		property_id: number;
		template: T;
		address: string | null;
		created_at: string;
	};

	export type Response = Array<Notification>;
}

interface SurveyFields {
	q_profile_seen_intro: OneZeroOrNull;
	question_deferred: OneZeroOrNull;
	question_deferred_desc: string | null;
	question_deferred_cost: number | null;
	question_purchased: OneZeroOrNull;
	question_purchased_desc: string | null;
	last_sale_price_prop: string | null;
	last_sale_date_prop: string | null;
	question_armslength: OneZeroOrNull;
	question_armslength_desc: string | null;
	question_income: OneZeroOrNull;
	q_primary_residence: OneZeroOrNull;
	q_receiving_exemptions: OneZeroOrNull;
	q_exemptions_list: string | null;
	q_exemptions_other: string | null;
	q_roof_replaced: OneZeroOrNull;
	q_roof_replaced_date: string | null;
	q_roof_replaced_desc: string | null;
	q_roof_replaced_cost: number | null;
	q_structural_issues: OneZeroOrNull;
	q_structural_issues_desc: string | null;
	q_structural_issues_list: string | null;
	q_structural_issues_cost: number | null;
	q_flooded: OneZeroOrNull;
	q_flooded_date: string | null;
	q_flooded_desc: string | null;
	q_flooded_cost: number | null;
	q_flooded_cause: string | null;
	q_remodeled: OneZeroOrNull;
	q_remodeled_type: string | null;
	q_remodeled_desc: string | null;
	q_remodeled_year: number | null;
	q_notice_received: OneZeroOrNull;
	q_negative_impact: OneZeroOrNull;
	q_negative_impact_list: string | null;
	q_negative_impact_cost: number | null;
	q_negative_impact_other: string | null;
	q_active_freeze: OneZeroOrNull;
	q_previously_appealed: OneZeroOrNull;
	q_appraisal: OneZeroOrNull;
	q_appraisal_value: number | null;
	q_appraisal_damage_value: number | null;
}

export type OneZeroOrNull = 1 | 0 | null;

export interface PropertyInsurance {
	insurance_id: number;
	marital_status: string;
	birthday: string;
	current_provider: string;
	current_deductible: string;
	quotes_ready_at: string;
	quotes_expired: 0 | 1;
	requested_contact: 1 | 0 | null;
	quotes: PropertyDetailsInsuranceQuote[];
}

interface NeedsAttentionData {
	name: NeedsAttentionReasons;
	documentsUnderReview: boolean | null;
	documentsFailedReview: boolean | null;
}

export interface FileData {
	file_name: string;
	question: string | null;
	is_ownwell_document: 1 | 0;
}

export interface PropertySurvey {
	appeal_id: number;
	q_current_homestead_status: string | null;
	q_major_damage: OneZeroOrNull;
	q_major_damage_list: string | null;
	q_major_damage_desc: string | null;
	q_major_damage_cost: number | null;
	q_major_damage_date: string | null;
	q_roof_damage: OneZeroOrNull;
	q_roof_damage_desc: string | null;
	q_roof_damage_cost: number | null;
	q_roof_damage_date: string | null;
	q_structural_issues_date: string | null;
	q_negative_impact_date: string | null;
	q_catchall_cost: number | null;
	q_catchall_date: string | null;
	q_yardsign: OneZeroOrNull;
	q_hearing_notice: OneZeroOrNull;
	q_hearing_notice_date: string | null;
}

export interface PropertyDetailsAppeal extends PropertySurvey {
	appeal_id: number;
	arbitration_session: string | null;
	customer_declined_survey: 1 | 0;
	customer_completed_survey: 1 | 0;
	docs_failed_review_reason: string | null;
	files: FileData[];
	final_assessed_value: string | null;
	final_market_value: string | null;
	final_tax_savings: string | null;
	hearing_status: string | null;
	last_sale_date_prop: string | null;
	last_sale_price_prop: string | null;
	market_value_to_beat: string | null;
	needs_attention: 1 | 0;
	needs_attention_last_checked_at: string | null;
	needs_attention_reason: NeedsAttentionData[];
	original_market_value: string | null;
	original_market_value_last_year: string | null;
	property_id: number;
	q_active_freeze: OneZeroOrNull;
	q_appraisal: OneZeroOrNull;
	q_appraisal_damage_value: number | null;
	q_appraisal_value: number | null;
	q_confirmed_deferred: 1 | 0;
	q_confirmed_details_stage_1: 1 | 0;
	q_confirmed_details_stage_2: 1 | 0;
	q_confirmed_income: 1 | 0;
	q_confirmed_purchase: 1 | 0;
	q_exemptions_list: string | null;
	q_exemptions_other: string | null;
	q_flooded: OneZeroOrNull;
	q_flooded_cause: string | null;
	q_flooded_cost: number | null;
	q_flooded_date: string | null;
	q_flooded_desc: string | null;
	q_negative_impact: OneZeroOrNull;
	q_negative_impact_cost: number | null;
	q_negative_impact_list: string | null;
	q_negative_impact_other: string | null;
	q_notice_received: OneZeroOrNull;
	q_previously_appealed: OneZeroOrNull;
	q_primary_residence: OneZeroOrNull;
	q_profile_seen_intro: 1 | 0;
	q_receiving_exemptions: OneZeroOrNull;
	q_remodeled: OneZeroOrNull;
	q_remodeled_desc: string | null;
	q_remodeled_type: string | null;
	q_remodeled_year: number | null;
	q_roof_replaced: OneZeroOrNull;
	q_roof_replaced_cost: number | null;
	q_roof_replaced_date: string | null;
	q_roof_replaced_desc: string | null;
	q_catchall: OneZeroOrNull;
	q_catchall_desc: string | null;
	q_structural_issues: OneZeroOrNull;
	q_structural_issues_cost: number | null;
	q_structural_issues_desc: string | null;
	q_structural_issues_list: string | null;
	q_wildcard_type: string | null;
	q_wildcard_answer: string | null;
	question_armslength: OneZeroOrNull;
	question_armslength_desc: string | null;
	question_deferred: OneZeroOrNull;
	question_deferred_cost: number | null;
	question_deferred_desc: string | null;
	question_income: OneZeroOrNull;
	question_purchased: OneZeroOrNull;
	//question_heardabout: string | null;
	reject_reason: string | null;
	case_reason: string | null;
	invoice_ref: string | null;
	invoice_amount: string | null;
	invoice_status: string | null;
	invoice_url: string | null;
	status: AppealStatus;
	tax_year: number;
	tax_rate: number | null;
	is_takeover: OneZeroOrNull;
	archive_reason: string | null;
	stage: string;
	is_litigation: boolean;
}

export interface PropertyDetailsInsuranceQuote {
	quote_id: number;
	premium: number;
	deductible: number | null;
	deductible_type: string | null;
	deductible_wind_hail: number | null;
	deductible_wind_hail_type: string | null;
	coverage_dwelling: number | null;
	coverage_other_structure: number | null;
	coverage_personal_property: number | null;
	coverage_loss_of_use: number | null;
	coverage_liability: number | null;
	coverage_medical: number | null;
	carrier_name: string;
	carrier_logo_url: string;
	requested_contact: OneZeroOrNull;
}

interface PropertyDetailsInsurance {
	birthday: string | null;
	credit_range: string | null;
	current_deductible: number | null;
	current_premium: number | null;
	current_provider: string | null;
	insurance_id: number | null;
	marital_status: string | null;
	monitoring_activated_at: string | null;
	quotes: PropertyDetailsInsuranceQuote[];
}

export interface BillReductionItem {
	id: number;
	category: string;
	bill_reduction_count: string;
	bill_reduction_created_at: string;
}

export interface LightEnrollment {
	property_id: number;
	total_dollars: string;
	total_kwh: string;
	contract_end_date: string | null;
	invoice_date: string;
	projected_payment: string;
	status: "completed" | "pending" | "failed" | "processing"; // Adjust statuses as needed
	enrolled: boolean;
	latest_bill_data?: {
		billAmount: string;
		billDueDate: string;
		billKwh: string;
	} | null;
	projected_bill_data: {
		message: {
			bill_year: number;
			bill_month: number;
			total_bill_usd: number;
			total_usage_kWh: number;
			projected_monthly_usage_kwh: number[];
			projected_monthly_bill_amounts: number[];
			percent_change_in_bill_from_current_month: number[];
			percent_change_in_usage_from_current_month_kwh: number[];
			projected_reduced_monthly_bill_amounts: number[];
			reduced_bill_usd: number;
			reduced_bill_ratio: number;
		};
	};
}

export interface PropertyDetails {
	address: string;
	address2: string | undefined;
	air_conditioning: string | undefined;
	apn: string | undefined;
	appeal_id: number;
	appeal_stage: string;
	appeals: PropertyDetailsAppeal[];
	arbitration_session: string | undefined;
	archive_reason: string | null;
	attic_sqft_fin: number | null;
	attic_sqft_unfin: number | null;
	auth_company: string;
	auth_company_type: string | null;
	auth_first_name: string;
	auth_last_name: string;
	auto_appeal: 1 | 0;
	available_services: Services.Id[];
	basement_sqft_fin: number | null;
	basement_sqft_unfin: number | null;
	baths: number | null;
	beds: number | null;
	bill_reduction: BillReductionItem[];
	light_enrollment: LightEnrollment | null;
	cancel_forever: 1 | 0;
	city: string;
	commercial: 1 | 0;
	construction: string | null;
	contingency: number;
	county: string;
	custom_msa: 1 | 0;
	customer_completed_survey: 1 | 0;
	customer_declined_survey: 1 | 0;
	customer_mark_as_sold_at: string | null;
	docs_failed_review_reason: string | null;
	driveway: string | null;
	end_date: string | null;
	exemption: PropertyDetailsExemptionFields | undefined;
	exemption_status: ExemptionStatus | null;
	accepting_exemptions: OneZeroOrNull;
	external_walls: string | null;
	final_market_value: string | null;
	final_tax_savings: string | null;
	fireplaces: number | null;
	garage_spaces: number | null;
	garage_type: string | null;
	has_deck: OneZeroOrNull;
	has_patio: OneZeroOrNull;
	has_pool: OneZeroOrNull;
	has_porch: OneZeroOrNull;
	heat: string | null;
	id: number;
	insurance: PropertyDetailsInsurance;
	insurance_available: 1 | 0;
	is_homestead: 1 | 0;
	homestead_fell_off_at: string | null;
	silence_homestead_fell_off_alert: 1 | 0;
	tax_page_available: 1 | 0;
	income_producing: 1 | 0;
	interior_walls: string | null;
	invoice_amount: string | null;
	invoice_id: number | null;
	invoice_status: string | null;
	invoice_url: string | null;
	invoice_type: string | null;
	exemption_final_refund_amount: string | null;
	exemption_invoice_ref: string | null;
	exemption_invoice_status: string | null;
	exemption_invoice_id: number | null;
	exemption_owner1_license: OneZeroOrNull;
	exemption_owner2_license: OneZeroOrNull;
	exemption_owner2_first_name: string | null;
	exemption_invoice_amount: string | null;
	exemption_needs_attention_reason: string | null;
	exemption_addl_docs_required: string | null;
	last_sale_date_prop: string | null;
	last_sale_price_prop: string | null;
	legal_owner: string | null;
	market_value_to_beat: string | null;
	mortgage_monitoring_enabled: 1 | 0;
	needs_attention: 1 | 0;
	needs_attention_last_checked_at: string | null;
	needs_attention_reason: NeedsAttentionData[];
	nickname: string | null;
	num_stories: string | null;
	num_units: number | null;
	original_market_value: string | null;
	original_market_value_last_year: string | null;
	owl_deadline: string | null;
	accepting_appeals: 1 | 0;
	exemption_requires_both_licenses: 1 | 0;
	q_appraisal: OneZeroOrNull;
	q_appraisal_damage_value: number | null;
	q_appraisal_value: number | null;
	q_confirmed_deferred: 1 | 0;
	q_confirmed_details_stage_1: 1 | 0;
	q_confirmed_details_stage_2: 1 | 0;
	q_confirmed_income: 1 | 0;
	q_confirmed_purchase: 1 | 0;
	q_flooded: OneZeroOrNull;
	q_flooded_cost: number | null;
	q_negative_impact: OneZeroOrNull;
	q_negative_impact_cost: number | null;
	q_notice_received: OneZeroOrNull;
	q_notice_received_date: string | null;
	q_primary_residence: OneZeroOrNull;
	q_profile_seen_intro: 1 | 0;
	q_receiving_exemptions: OneZeroOrNull;
	q_remodeled: OneZeroOrNull;
	q_roof_replaced: OneZeroOrNull;
	q_roof_replaced_cost: number | null;
	q_structural_issues: OneZeroOrNull;
	q_wildcard_answer: string | null;
	q_wilcard_type: string | null;
	q_catchall: OneZeroOrNull;
	q_catchall_desc: string | null;
	question_deferred_cost: number | null;
	question_purchased: OneZeroOrNull;
	raw_apn: string | null;
	raw_address: string | null;
	reauthorization_session: string | null;
	reject_reason: string | null;
	roof_type: string | null;
	sewer_type: string | null;
	sqft_portal_comm_gross: number | null;
	sqft_portal_comm_rentable: number | null;
	sqft_portal_land: number | null;
	sqft_portal_resi_imprv: number | null;
	stage: string;
	state: string;
	status: AppealStatus;
	tax_year: number;
	use_group: string | null;
	use_info: string | null;
	verified_owner: 1 | 0;
	water_type: string | null;
	year_built: number | null;
	year_renovated: number | null;
	zip_code: string | null;
	customer_evidence_last_modified_at: string | null;
	num_new_crime_incidents: number | null;
	active_invoices: Array<{
		stripe_ref: string | null;
		total_savings: string | null;
		status: string | null;
		hosted_url: string | null;
	}>;
	customer_provided_type:
		| null
		| string
		| "single-family"
		| "multi-family"
		| "condo"
		| "manufactured";
	customer_provided_use:
		| null
		| string
		| "primary-residence"
		| "vacation-home"
		| "investment-property";
	onboarding_survey_completed: 1 | 0;
	website_estimated_savings: string | null;
}

export namespace Dashboard {
	export namespace GetProperties {
		export type Response = {
			properties: Array<{
				property_id: number;
				address: string;
				apn: string;
				county: string;
				state: string;
				appeal_id: number;
				tax_year: number;
				status: AppealStatus;
				appeal_created_at: string;
				original_market_value_last_year: string | null;
				original_market_value: string | null;
				final_tax_savings: string | null;
				percent_increase: string | null;
				final_market_value: string | null;
				tax_bill: string | null;
				dollar_reduction: string | null;
				percent_reduction: string | null;
				needs_attention: 0 | 1;
				needs_attention_reason: NeedsAttentionData[];
				auth_company: string;
				appeal_stage: string;
				auto_appeal: 0 | 1;
				accepting_appeals: 0 | 1;
				customer_mark_as_sold_at: string | null;
				owl_deadline: string | null;
				verified_owner: 0 | 1;
				legal_owner: string | null;
				auth_company_type: string | null;
				archive_reason: string | null;
				enterprise_external_id: string | null;
				is_litigation: 0 | 1;
				cancel_forever: 0 | 1;
			}>;
			counts: {
				total_appeals: number;
				num_filtered_properties: number;
				num_total_pages: number;
				completed_appeals: number;
				total_assessment_reduction: number;
				final_tax_savings: number;
				avg_dollar_reduction: number;
				num_verifying: number;
				num_monitoring: number;
				num_underassessed: number;
				num_not_filed: number;
				num_canceled: number;
				num_evaluating: number;
				num_filing: number;
				num_hearing: number;
				num_savings: number;
				num_no_change: number;
				num_new_signature_required: number;
				num_owner_info_mismatch: number;
				num_closing_disclosure_needed: number;
				num_entity_docs_needed: number;
				num_county_records_update_needed: number;
				num_notice_of_value_needed: number;
				num_income_information_needed: number;
				num_condition_information_needed: number;
				num_documents_under_review: number;
			};
			reductions: {
				property_id: number;
				address: string;
				original_market_value: string;
				dollar_reduction: string;
				percent_reduction: string;
				tax_year: number;
			}[];
			meta: {
				states: string[];
				legal_entities: string[];
				tax_years: number[];
			};
		};
	}
}

export namespace AllActiveProperty {
	export namespace GetProperties {
		export type Response = {
			properties: Array<{
				property_id: number;
				address: string;
				apn: string;
				county: string;
				city: string;
				zip_code: string;
				state: string;
				appeal_id: number;
				tax_year: number;
				status: AppealStatus;
				appeal_created_at: string;
				original_market_value_last_year: string | null;
				original_market_value: string | null;
				final_tax_savings: string | null;
				percent_increase: string | null;
				final_market_value: string | null;
				tax_bill: string | null;
				dollar_reduction: string | null;
				percent_reduction: string | null;
				needs_attention: 0 | 1;
				needs_attention_reason: NeedsAttentionData[];
				auth_company: string;
				appeal_stage: string;
				auto_appeal: 0 | 1;
				accepting_appeals: 0 | 1;
				customer_mark_as_sold_at: string | null;
				owl_deadline: string | null;
				verified_owner: 0 | 1;
				legal_owner: string | null;
				auth_company_type: string | null;
				archive_reason: string | null;
				enterprise_external_id: string | null;
				is_litigation: 0 | 1;
				cancel_forever: 0 | 1;
			}>;
			meta: {
				states: string[];
				counties: string[];
			};
			counts: {
				num_results: number;
				num_pages: number;
				num_results_per_page: number;
				num_active_properties: number;
			};
		};
	}
}

export namespace AllActiveAppeals {
	export namespace GetAppeals {
		export interface Response
			extends Omit<AllActiveProperty.GetProperties.Response, "counts"> {
			meta: {
				states: string[];
				counties: string[];
				tax_years: string[];
			};
			counts: {
				num_results: number;
				num_pages: number;
				num_results_per_page: number;
			};
		}
	}
}

export namespace ActionsRequired {
	export namespace GetActionsRequired {
		export type Response = {
			appeal_nars: Array<{
				needs_attention_reason: string;
				needs_attentioni_reason_title: string;
				needs_attention_deadline: string | null;
				needs_attention_deadline_computed: string | null;
				needs_attention_task_status: "to do" | "urgent";
				documents_under_review: boolean | null;
				documents_failed_review: boolean | null;
				enterprise_exteneral_id: string;
				legal_owner: string | null;
				verified_owner: 0 | 1;
				auth_company: string | null;
				auth_company_type: string | null;
				address: string;
				apn: string;
				county: string;
				state: string;
				property_id: number;
				appeal_id: number;
			}>;
			counts: {
				num_nars_urgent: number;
				num_nars_total: number;
				num_nars_under_review: number;
				num_nars_failed_review: number;
				num_results: number;
				num_pages: number;
				num_results_per_page: number;
			};
			meta: {
				states: string[];
				tax_years: number[];
				task_types: string[];
				task_review_statuses: string[];
				task_statuses: string[];
			};
		};
	}

	export namespace GetActionsRequiredCounts {
		export type Response = {
			num_nars_urgent: number;
			num_nars_total: number;
			num_nars_under_review: number;
			num_nars_failed_review: number;
		};
	}
}

type PIIRefType = "ssn_tin" | "ssn" | "ssn_last_4" | "green_card";
export type ExemptionPIIRefType =
	| `owner1_${PIIRefType}`
	| `owner2_${PIIRefType}`;

export interface PropertyDetailsExemptionFields {
	exemption_id: number | null;
	is_primary_residence: OneZeroOrNull;
	current_homestead_status: string | null;
	should_transfer_status: OneZeroOrNull;
	previous_address: string | null;
	previous_address2: string | null;
	previous_city: string | null;
	previous_state: string | null;
	previous_zip_code: string | null;
	previous_county: string | null;
	previous_move_out_date: string | null;
	purchase_date: string | null;
	move_in_date: string | null;
	birth_date: string | null;
	is_inherited_property: OneZeroOrNull;
	other_heir_owners_occupy: OneZeroOrNull;
	has_disability: OneZeroOrNull;
	is_veteran: OneZeroOrNull;
	surviving_spouse_of_65_plus_person: OneZeroOrNull;
	disabled_veteran_or_surviving_spouse: OneZeroOrNull;
	surviving_spouse_of_veteran_killed_in_line_of_duty: OneZeroOrNull;
	surviving_spouse_of_first_responder_killed_in_line_of_duty: OneZeroOrNull;
	donated_residence_of_partially_disabled_vet_or_surviving_spouse: OneZeroOrNull;
	owned_through_cooporative_housing: OneZeroOrNull;
	owned_through_non_profit: OneZeroOrNull;
	collect_rent_at_this_property: OneZeroOrNull;
	percent_income_producing: number | null;
	ownership_status: string | null;
	marital_status: string | null;
	owner2_first_name: string | null;
	owner2_last_name: string | null;
	owner2_email: string | null;
	owner2_phone: string | null;
	owner2_birth_date: string | null;
	owner1_percent_ownership: number | null;
	owner2_percent_ownership: number | null;
	owner1_license_ref: string | null;
	owner2_license_ref: string | null;
	owner1_license_number_ref: string | null;
	owner2_license_number_ref: string | null;
	owner1_has_uploaded_license: OneZeroOrNull;
	owner2_has_uploaded_license: OneZeroOrNull;
	owner1_pii_ref: ExemptionPIIRefType | null;
	owner2_pii_ref: ExemptionPIIRefType | null;
	other_property_counties: string | null;
	solar_or_wind_installed: OneZeroOrNull;
	solar_or_wind_has_current_exemptions: OneZeroOrNull;
	solar_or_wind_interested_in_installing: OneZeroOrNull;
	owner_driver_license_exempt: OneZeroOrNull;
	owner2_driver_license_exempt: OneZeroOrNull;
	signed_at: string | null;
	rare_situations_none_of_the_above: OneZeroOrNull;
	retro_homestead_savings_estimate: number | null;
	archive_reason: string | null;
	manufactured_home_make: string | null;
	manufactured_home_model: string | null;
	manufactured_home_id: string | null;
	transfer_move_out_date: string | null;
	transfer_sale_date: string | null;
	transfer_owner_did_not_sell_property: OneZeroOrNull;
	is_end_to_end_signup: 1 | 0;
	special_fields: null | {
		us_citizen?: boolean;
		is_widow?: boolean;
		is_blind?: boolean;
		is_65_years_or_older?: boolean;
		is_disabled_first_responder_or_surviving_spouse_of_a_first_responder?: boolean;
		is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty?: boolean;
		is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran?: boolean;
		is_a_disabled_veteran_confined_to_a_wheelchair?: boolean;
		is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran?: boolean;
		is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty?: boolean;
		disabled_veteran?: boolean;
		recently_returned_from_active_duty?: boolean;
		was_resident_of_assisted_living_facility?: boolean;
		owned_by_trust?: boolean;
		trust_document_ref?: string;
		residency_proof_document_refs?: string[];
		residency_proof_document_types?: string[];
		exemption_transfer_reason?: string;
		transfer_star_credit?: string;
		is_manufactured_home?: boolean;
		previous_address_apn?: string;
		previous_address_homestead?: boolean;
		previous_address_file_portability?: boolean;
		make_less_than_67k?: boolean;
		is_commercial_property?: boolean;
		surviving_spouse_of_a_veteran?: boolean;
		self_or_spouse_has_out_of_state_tax_return?: boolean;
		self_or_spouse_out_of_state_tax_return_state?: string;
		already_filed?: boolean;
		income_tax_history?: Array<{
			tax_year?: number;
			annual_household_income?: number;
			owner1?: {
				state?: string;
				gross_income?: number;
				taxable_income?: number;
				received_refund?: boolean;
				refund_amount?: number;
				filing_status?: string;
				tax_return_type?: string;
				federal_tax_return?: boolean;
			};
			owner2?: {
				state?: string;
				gross_income?: number;
				taxable_income?: number;
				received_refund?: boolean;
				refund_amount?: number;
				filing_status?: string;
				tax_return_type?: string;
				federal_tax_return?: boolean;
			};
		}>;
		spouse_address?: {
			address?: string;
			address2?: string;
			city?: string;
			state?: string;
			zip_code?: string;
		};
		vehicle_registrations?: Array<{ registration_id: string; county: string }>;
		vehicle_registration_uploaded?: boolean;
		vehicle_info?: Array<{
			make?: string;
			model?: string;
			year?: string;
			license_plate_number?: string;
			county_where_registered?: string;
			state_where_registered?: string;
			state_where_owned?: string;
		}>;
		surviving_spouse_or_child_of_a_veteran?: boolean;
		//PA
		is_property_used_for_commercial_agricultural_purposes?: boolean;
		is_co_op_where_some_taxes_are_paid_jointly?: boolean;
		percent_of_co_op_ownership?: number;
		is_property_used_as_business_or_rental_property?: boolean;
		percent_of_square_footage_used_as_business_or_rental_property?: number;
		municipality?: string;
		school_district?: string;
		is_age_18_or_older_disabled_and_under_45k_per_year?: boolean;
		is_age_65_or_older_and_under_45k_per_year?: boolean;
		is_surving_spouse_of_a_deceased_partner_age_50_or_older?: boolean;
		is_active_duty_personnel?: boolean;
		// FL
		current_employer?: string;
		previous_tax_return_address?: {
			address?: string;
			address2?: string;
			city?: string;
			state?: string;
			zip_code?: string | null;
		};
		// NY
		has_ny_gov_id_account?: boolean;
		does_not_want_to_provide_ny_gov_id_account_credentials?: boolean;
		ny_gov_id_account_username_provided?: boolean;
		ny_gov_id_account_password_provided?: boolean;
		filed_ny_state_tax_return_within_last_5_years?: boolean;
		most_recent_ny_state_tax_return?: {
			filing_status?: string;
			tax_year?: number;
			gross_income?: number;
			taxable_income?: number;
			received_refund?: boolean;
			refund_amount?: number;
			requested_refund?: boolean;
			refund_amount_owed?: number;
			refund_amount_requested?: number;
			total_payments_amount?: number;
		};
	};
	needs_attention_reason: string;
	addl_docs_required: string;
}

// export interface PropertyDetails extends GetPropertiesProperty {
// 	id: number;
// 	appeal_id: number;
// 	appeals: GetPropertiesAppeal[];
// 	apn: string | undefined;
// 	address: string;
// 	address2: string | undefined;
// 	city: string;
// 	state: string;
// 	zip_code: string;
// 	county: string;
// 	commercial: boolean;
// 	use_group: string;
// 	use_info: string;
// 	status: AppealStatus;
// 	beds: string | undefined;
// 	baths: string | undefined;
// 	needs_attention: boolean;
// 	nickname: string | undefined;
// 	tax_year: number;
// 	year_built: number | undefined;
// 	year_renovated: number | undefined;
// 	garage_type: string | undefined;
// 	garage_spaces: number | undefined;
// 	attic_sqft_fin: number | undefined;
// 	attic_sqft_unfin: number | undefined;
// 	basement_sqft_fin: number | undefined;
// 	basement_sqft_unfin: number | undefined;
// 	has_pool: number | undefined;
// 	has_patio: number | undefined;
// 	has_deck: number | undefined;
// 	has_porch: number | undefined;
// 	interior_walls: string | undefined;
// 	air_conditioning: string | undefined;
// 	heat: string | undefined;
// 	roof_type: string | undefined;
// 	fireplaces: string | undefined;
// 	external_walls: string | undefined;
// 	driveway: string | undefined;
// 	construction: string | undefined;
// 	sewer_type: string | undefined;
// 	water_type: string | undefined;
// 	num_units: number | undefined;
// 	sqft_portal_land: number | undefined;
// 	sqft_portal_resi_imprv: number | undefined;
// 	sqft_portal_comm_gross: number | undefined;
// 	sqft_portal_comm_rentable: number | undefined;
// 	net_rentable_sqft: number | undefined;
// 	auth_first_name: string;
// 	auth_last_name: string;
// 	auth_company: string | undefined;
// 	final_tax_savings: string | null;
// 	final_market_value: string | null;
// 	original_market_value: string | null;
// 	original_market_value_last_year: string | null;
// 	num_stories: number | undefined;
// 	reject_reason: string | null;
// 	arbitration_session: string | null;
// 	insurance?: PropertyInsurance;
// 	invoice_amount: string | null;
// 	invoice_url: string | null;
// 	invoice_status: string | null;
// 	auto_appeal: 1 | 0;
// 	customer_mark_as_sold_at: string | null;
// }

export interface ReauthorizationData {
	first_name: string;
	last_name: string;
	company: string;
	contingency: number;
	address: string;
	city: string;
	state: string;
	zip_code: string;
	county: string;
	apn: string;
	email: string;
	phone: string;
	legal_owner: string | undefined;
	company_title: string | undefined;
	referral_code: string;
	sigonly: 1 | 0;
}

export namespace Invoices {
	export interface Invoice<
		T =
			| AppealLineItem
			| AppealEscalationLineItem
			| ExemptionLineItem
			| BillReductionLineItem
			| CreditLineItem
			| CustomLineItem,
	> {
		meta: {
			flat_fee: number;
			id: number;
			amount: string;
			status: string;
			due: string;
			created: string;
			url: string | null;
			paid: string | null;
			credit_referral: number | null;
			credit_review: number | null;
			credit_already_paid: number | null;
			has_invoice_breakdown: 1 | 0;
			invoice_ref: string;
		};
		line_items: Array<T>;
		other_invoices?: Array<{ id: number; amount: number; hosted_url: string }>;
		account_tier: string | null;
		customer_id: number;
	}

	export type Collection = Record<string, Invoice>;

	export interface LineItem {
		type: string;
		address: string;
		city: string;
		state: string;
		status: string;
		zip_code: string;
		county: string;
		contingency: number;
		tax_rate: number | null;
		tax_year: number | null;
		original_market_value: string | null;
		final_market_value: string | null;
		final_tax_savings: string | null;
		flat_fee: string | null;
		bill_reduction: null | {
			current_bill: number;
			new_bill: number;
			num_reduced_months: number;
			category: string;
			provider: string;
			total_savings: number;
		};
		description: string | null;
		amount_due: string | null;
	}

	export interface BillReductionLineItem extends LineItem {
		type: "bill_reduction";
		bill_reduction: {
			current_bill: number;
			new_bill: number;
			num_reduced_months: number;
			category: string;
			provider: string;
			total_savings: number;
		};
	}

	export interface AppealLineItem extends LineItem {
		type: "appeal";
		tax_rate: number;
		tax_year: number;
		original_market_value: string;
		final_market_value: string;
		final_tax_savings: string;
		flat_fee: string;
	}

	export interface AppealEscalationLineItem extends LineItem {
		type: "appeal_escalation";
		tax_rate: number;
		tax_year: number;
		original_market_value: string;
		final_market_value: string;
		final_tax_savings: string;
		flat_fee: string;
	}

	export interface ExemptionLineItem extends LineItem {
		type: "exemption";
		final_tax_savings: string;
		flat_fee: string;
	}

	export interface CreditLineItem extends LineItem {
		type: "credit";
		amount_due: string;
		contingency: number;
		description: string;
	}

	export interface CustomLineItem extends LineItem {
		type: "custom";
		description: string;
		amount_due: string;
	}
}

export interface ArbitrationUserData {
	first_name: string;
	last_name: string;
	company: string;
	address: string;
	city: string;
	state: string;
	zip_code: string;
	county: string;
	email: string;
	phone: string;
	apn: string;
	taxacct: string;
	tax_year: string;
	original_market_value: number;
	recommended_market_value: number;
	contingency: string;
	account_tier: string | null;
	account_rep: OneZeroOrNull;
	pricing_premium_appeal_contingency: number | null;
	is_homestead: 1 | 0;
}

export interface InsuranceAnswerReducerState {
	marital_status: string;
	birthday: string;
	// credit_range: string;
	current_provider: string;
	current_premium: string;
	current_deductible: string;
	// discounts: { value: string[] };
}

export type InsuranceQuestionID = keyof InsuranceAnswerReducerState;

export type NeedsAttentionReasons =
	| "Wrong Owner"
	| "Notice of Value"
	| "Cannot Verify Owner"
	| "Cannot Verify Owner Entity"
	| "Need Closing Docs"
	| "Need Income Info"
	| "Need Condition Info"
	| "Authorization Expired"
	| "Arbitration"
	| "Entity Docs Needed (Trust / Estate / Corp)"
	| "Update County Ownership Record";

// export interface InsuranceQuote {
// 	uid: string;
// 	premium: string;
// 	carrier: {
// 		description: string;
// 		key: string;
// 		logo_url: string;
// 		name: string;
// 	};
// 	coverages: {
// 		coverage_a: string;
// 		coverage_b: string;
// 		coverage_c: string;
// 		coverage_d: string;
// 		coverage_e: string;
// 		coverage_f: string;
// 	};
// 	deductibles: {
// 		value: string;
// 		type: "flat" | "percentage";
// 		perils: "all" | "windhail";
// 	}[];
// }

export interface CountyData {
	county: {
		prop_search_url: string | null;
		apn_template: string | null;
	} | null;
}

export type SavingsType = "exact" | "county" | "state";

export interface FileUploadProps {
	file_name: string;
	tax_year: number;
	is_ownwell_document: 1 | 0;
}

export type SavingsCopyType =
	| "unknown-assessment"
	| "targeted"
	| "non-targeted"
	| "exemption-or-zero"
	| "non-apn"
	| "low-savings"
	| "no-chance-of-savings"
	| "skip"
	| null;

export type ExemptionsSavingsResponse = {
	fips: string;
	apn: string;
	savings: number;
	retroSavings: number;
	futureSavings: number;
	metadata: {
		taxYear: string;
		homestead: boolean;
		taxRate: number;
		reduction: number;
		savings: number;
		flatSavings: number;
		oldAssessedValue: number;
		newAssessedValue: number;
	}[];
};

export type ExemptionCurrentlyReceivingValues =
	| "no"
	| "notSure"
	| "yesAnotherProperty"
	| "yesAnotherPropertyInAnotherState"
	| "yesAnotherPropertyInCalifornia"
	| "yes";

export interface LeftoverProperty {
	situs_address: string;
	situs_city: string;
	situs_state: string;
	situs_zip: string;
	apn: string;
	savings: number;
}

export interface PricingData {
	county: string;
	state: string;
	in_service: boolean;
	pricing: {
		accepting_appeals: boolean;
		appeal_contingency: number | null;
		appeal_flat: number | null;
		accepting_exemptions: boolean;
		exemption_primary_contingency: number | null;
		exemption_primary_flat: number | null;
		premium_appeal_contingency: number | null;
		premium_exemption_primary_contingency: number | null;
		// exemption_primary_retro_pricing_contingency: number | null;
		// exemption_primary_retro_pricing_flat: number | null;
	};
}

export type CustomerPricingData = Array<
	{
		property_id: number;
		current: {
			contingency_locked: 0 | 1;
			appeal_contingency: number | null;
			appeal_flat: number | null;
			exemption_contingency: number | null;
			exemption_flat: number | null;
		};
	} & PricingData
>;

interface ExpensesNeighborhoodComparisonEntry {
	percentile25: number;
	percentile75: number;
}

export interface ExpensesData {
	estimatedElectricityCost: number;
	estimatedGasCost: number;
	estimatedInsurancePremium: number;
	estimatedMortgageRate: number;
	estimatedPMICost: number;
	estimatedUtilitiesCost: number;
	estimatedWaterCost: number;
	neighborhoodComparison: {
		mortgage: ExpensesNeighborhoodComparisonEntry;
		insurance: ExpensesNeighborhoodComparisonEntry;
		pmi: ExpensesNeighborhoodComparisonEntry;
		utilities: ExpensesNeighborhoodComparisonEntry;
	};
	userData: {
		insurance: number | null;
		pmi: number | null;
		utilities: number | null;
		escrow: number | null;
		property_tax: number | null;
		in_escrow: boolean | null;
	};
}

export type SignUpType =
	| "appeal_only"
	| "appeal_and_exemption"
	| "exemption_only";

export type InsuranceStatus =
	| "BEFORE_SURVEY"
	// | "QUOTES_LOADING"
	| "QUOTES_READY"
	// | "NO_QUOTES"
	// | "QUOTE_SELECTED"
	// | "POLICY_ADDED"
	// | "EXPIRED"
	| "ACTIVATED";

export interface CrimeAlertIncident {
	city_key: string;
	incident_code: string;
	incident_date: string;
	incident_offense: string;
	incident_offense_description: string;
	incident_offense_code: string;
	incident_description: string;
	incident_offense_detail_description: string;
	incident_offense_crime_against: string;
	incident_offense_action: string;
	incident_source_original_type: string;
	incident_source_name: string;
	incident_latitude: number;
	incident_longitude: number;
	incident_address: string;
	incident_category: string;
	incident_distance: string;
}

export interface NeighborhoodPermitData {
	id: number;
	description: string;
	permit_number: string;
	jurisdiction: string;
	permit_type: string;
	fees: string;
	status: string;
	file_date: string;
	issue_date: string;
	final_date: string;
	construction_duration: string;
	approval_duration: string;
	contractor_id: string;
	address: string;
	city: string;
	zip_code: string;
	state: string;
	lat: number;
	lng: number;
	tags: string[];
	distance: number;
}

export interface NeighborhoodPermitsResponse {
	permits: NeighborhoodPermitData[];
	property: {
		lat: string;
		lng: string;
	};
	trends: {
		num_residential_ytd: number;
		num_commercial_ytd: number;
	};
	last_updated: string;
}

export interface CrimeTrendsEntry {
	month: number | string;
	year: number;
	category: string;
	count: number;
}

export interface AppealsLoggingData {
	address: string;
	addressRaw: string | undefined;
	assessment: number | undefined;
	city: string;
	state: string;
	zip: string;
	session: string;
	session_id: string;
	savings: string | null;
	isMailer: boolean | undefined;
	county: string | undefined;
	fname: string | null;
	lname: string | null;
	company: string | null;
	phone: string | null;
	email: string | null;
	past_deadline: boolean;
	line2: string;
	referrer: string;
	landing: string;
	apn: string | undefined;
	referralCode: string | undefined;
	campaign: string | undefined;
	discount: number | undefined;
	savingsType: SavingsType | undefined;
	savingsCopyType: SavingsCopyType | "skip" | undefined;
	partner_ref: string | undefined;
	showSavingsBox: boolean | null;
	ai_video_ab_test: boolean | null;
	ai_video_name: string | null;
	ai_video_clicked: boolean | null;
	use_group: string | null;
	sign_up_session_data?: object | null;
}

export type SEOTable<T> = {
	name: string;
	description: string;
	columns: { name: string; ref: string; datatype: string }[];
	data: Array<T>;
};

export type TrendsSummaryData = null | {
	total_sales: string | null;
	turnover_pct: string | null;
	median_home_price: number | null;
	median_tax_rate_percent: string | null;
	median_tax_bill: number | null;
	tax_bill_25th_percentile: number | null;
	tax_bill_75th_percentile: number | null;
	tax_bill_90th_percentile: number | null;
	median_mortgage_rate: number | null;
	median_mortgage_rate_25th_percentile: number | null;
	median_mortgage_rate_75th_percentile: number | null;
	median_mortgage_rate_90th_percentile: number | null;
	highest_median_tax_rate?: {
		highest_tax_rate_percent: string;
		county_name: string;
	};
	lowest_median_tax_rate?: {
		lowest_tax_rate_percent: string;
		county_name: string;
	};
	highest_median_tax_bill?: {
		highest_median_tax_bill: string;
		county_name: string;
	};
	lowest_median_tax_bill?: {
		lowest_median_tax_bill: string;
		county_name: string;
	};
};

export type USTrendsSummaryData = {
	median_home_price: number | null;
	median_tax_rate_percent: string | null;
	median_tax_bill: number | null;
};

export type TrendsData = {
	state: string;
	state_name: string;
	state_fips: string;
	county: string | null;
	county_fips: string | null;
	city: string | null;
	zip: string | null;
	turnover_pct: string | null;
	total_sales: string | null;
	is_valid: boolean;
	visible_trends_page: boolean;

	tax_rate_10th_percentile: number | null;
	tax_rate_25th_percentile: number | null;
	median_tax_rate: number | null;
	tax_rate_75th_percentile: number | null;
	tax_rate_90th_percentile: number | null;

	home_price_10th_percentile: number | null;
	home_price_25th_percentile: number | null;
	median_home_price: number | null;
	home_price_75th_percentile: number | null;
	home_price_90th_percentile: number | null;

	tax_bill_10th_percentile: number | null;
	tax_bill_25th_percentile: number | null;
	median_tax_bill: number | null;
	tax_bill_75th_percentile: number | null;
	tax_bill_90th_percentile: number | null;

	median_mortgage_rate_10th_percentile: number | null;
	median_mortgage_rate_25th_percentile: number | null;
	median_mortgage_rate: number | null;
	median_mortgage_rate_75th_percentile: number | null;
	median_mortgage_rate_90th_percentile: number | null;
};

export type TrendsSEOTable = SEOTable<Partial<TrendsData>>;
export interface StatePageCountyInfo {
	county: string;
	notice_date: string | undefined;
	deadline: string | undefined;
}

export type LifeTimeSavingsResponse = {
	appeal_savings: number;
	exemption_refund: number;
	bill_reduction_savings: number;
	exemption_flat_savings: number;
	total_savings: number;
};

export type PropertiesSavingsData = Record<number, LifeTimeSavingsResponse>;

export type GetTrendsDataResponse = {
	averageAreaSavings: number;
	averageAreaPercentageReduction?: number;
	supportedServices: {
		accepting_appeals: OneZeroOrNull;
		accepting_exemptions: OneZeroOrNull;
	};
	usSummaryData?: USTrendsSummaryData | null;
	stateSummaryData?: TrendsSummaryData | null;
	countySummaryData?: TrendsSummaryData | null;
	citySummaryData?: TrendsSummaryData | null;
	tableData: Array<TrendsData>;
	textContent: Record<string, string> | null;
	mapData: Array<TrendsData> | null;
	countyInfo: CountyDatesAndLinks | null;
	comparisonData: Array<TrendsData> | null;
};

export const signUpEntryPoints = [
	"default",
	"appeal",
	"exemption",
	"insurance",
	"bill-reduction",
] as const;

export type SignUpEntryPoint = (typeof signUpEntryPoints)[number];

export type SignUpSavingsVariant = {
	type: "appeal" | "universal-savings";
	primarySavingsBox: SignUpEntryPoint;
	appealsInService: boolean;
	exemptionsInService: boolean;
};

export interface DynamicLandingPageProps {
	dynamicElements?: {
		title?: string | null;
		subtitle?: string | null;
		image?: {
			fields: {
				description: string;
				file: {
					url: string;
				};
			};
		};
		showBelowFold?: boolean | null;
		savingsButtonCta?: string | null;
		underAddressInputCopy?: string | null;
	};
}

export interface DynamicCommercialLandingPageProps {
	dynamicElements?: {
		heroTitle?: string | null;
		subTitle?: string | null;
		heroImage?: {
			fields: {
				description: string;
				file: {
					url: string;
				};
			};
		};
		showSignupCta?: boolean;
		showTalkToSalesCta?: boolean;
		showTestimonialsSection?: boolean;
		showAssetTypeSection?: boolean;
		showAdvantagesSection?: boolean;
		showAppealProcessSection?: boolean;
		showPTCSection?: boolean;
		showCaseStudiesSection?: boolean;
	};
}

export type DeepPartial<T> = T extends object
	? {
			[P in keyof T]?: DeepPartial<T[P]>;
		}
	: T;

export type Nullable<T> = { [K in keyof T]: T[K] | null };

export type CompanyPartnerDetails = {
	name: string;
	"display-name": string;
	href?: string;
	image: string | null;
};

export type ReferralRealtorPartnerDetails = {
	name: string;
	photo: string | undefined;
	phone: string;
	agency: string;
};

export type PartnerPhoto = {
	fields: {
		file: {
			url: string;
		};
	};
};

export type NonEmptyArray<T> = [T, ...T[]];
export const isNonEmptyArray = <T>(
	arr: T[] | null | undefined
): arr is NonEmptyArray<T> => Array.isArray(arr) && arr.length > 0;

export namespace CanopyConnect {
	export namespace Pulls {
		export type GetResponse = Array<{
			canopy_connect_pull_id: number;
			status: string;
			embed_session_id: string;
			pull_id?: string;
			insurance_provider_name?: string;
			insurance_provider_friendly_name?: string;
		}>;
	}
}

export type ContentfulEntryType = "insight" | "blog" | "casestudy";
